import { Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import type { NavigateOptions, To } from 'react-router-dom';
import { createBrowserRouter, Outlet } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { AuthProvider } from 'auth/helpers';
import type { MotionConfigProps } from 'framer-motion';
import { MotionConfig } from 'framer-motion';

import {
  DocumentFiltersProvider,
  ErrorBoundary,
  ModalsRoot,
  NabanGlossaryModal,
} from '@izimi/components';
import { ModulesProvider } from '@izimi/modules';
import { Fonts } from '@izimi/theme';
import modalEntities from 'common/modals/modalEntities';
import { NotaryTrackingProvider } from 'common/providers';
import { RealtimeCacheSync } from 'core/cache/RealtimeCacheSync';
import { CookieProvider } from 'core/tracking/cookies/CookieProvider';
import { Routes } from 'navigation/types';

import { modules } from '../core/modules';
import { routes } from './routes';
import '@izimi/components/src/tracking/cookies/styles.css';
import '@izimi/components/src/DatePicker/chakra-react-datepicker.css';

const motionConfig: MotionConfigProps = {
  reducedMotion: 'user',
  transition: { type: 'tween', duration: 0.15 },
};

const RootLayout = () => {
  const { t } = useTranslation('errors');

  return (
    <>
      <Fonts />
      <ErrorBoundary t={t} rerouteTo={Routes.Vault}>
        <ModulesProvider modules={modules}>
          <AuthProvider>
            <RealtimeCacheSync />
            <CookieProvider>
              <MotionConfig {...motionConfig}>
                <DocumentFiltersProvider>
                  <NotaryTrackingProvider>
                    <Suspense>
                      <ModalsRoot entities={modalEntities} />
                    </Suspense>
                    <Outlet />
                    <NabanGlossaryModal />
                  </NotaryTrackingProvider>
                </DocumentFiltersProvider>
              </MotionConfig>
            </CookieProvider>
          </AuthProvider>
        </ModulesProvider>
      </ErrorBoundary>
    </>
  );
};

export const router = Sentry.wrapCreateBrowserRouter(createBrowserRouter)(
  [
    {
      element: <RootLayout />,
      children: routes,
    },
  ],
  {
    v7_startTransition: true,
  },
);

export const historyNavigate = (to: To, options?: NavigateOptions) => {
  router.navigate(to, options);
};

export const historyReplace = (to: To) => {
  router.navigate(to, { replace: true });
};
